import type {
  ColorParameter,
  GeneralSettingsResponse,
  GeneralSettingsUpdate,
  InterruptionSettings,
  ProjectConfigResponse,
  ThemeData,
  TimeDisplaySettings,
} from './types';
import { apiClient } from '@/shared/api';

import themeConfigJson from '../../theme-config.json';

/**
 * Локальная конфигурация проекта — единственный источник запасных значений общих настроек,
 * пока конфигурация не пришла с бекенда и когда бекенд недоступен.
 */
export const LOCAL_PROJECT_CONFIG = themeConfigJson as ProjectConfigResponse;

/**
 * Слияние параметров цвета по code: локальные — база, с бекенда перезаписывают по code.
 */
const mergeColorParametersByCode = (
  localParams: ColorParameter[],
  serverParams: ColorParameter[] | undefined,
): ColorParameter[] => {
  const byCode = new Map(localParams.map((p) => [p.code, { ...p }]));
  if (serverParams?.length) {
    for (const p of serverParams) {
      byCode.set(p.code, { ...p });
    }
  }
  return Array.from(byCode.values());
};

/**
 * Слияние одной темы по code: локальная — база, поля и colorParameter с бекенда перезаписывают.
 */
const mergeThemeByCode = (localTheme: ThemeData, serverTheme: ThemeData | undefined): ThemeData => {
  if (!serverTheme || serverTheme.code !== localTheme.code) {
    return localTheme;
  }
  return {
    id: serverTheme.id ?? localTheme.id,
    code: localTheme.code,
    name: serverTheme.name ?? localTheme.name,
    logoSvg: serverTheme.logoSvg ?? localTheme.logoSvg,
    colorParameter: mergeColorParametersByCode(localTheme.colorParameter, serverTheme.colorParameter),
  };
};

/**
 * Общие настройки системы с бекенда; при отсутствии значения — локальное.
 */
const mergeGeneralSettings = (
  local: ProjectConfigResponse,
  server: ProjectConfigResponse | null,
): InterruptionSettings & TimeDisplaySettings => ({
  timeDisplayFormat: server?.timeDisplayFormat ?? local.timeDisplayFormat,
  playerTimeDisplayFormat: server?.playerTimeDisplayFormat ?? local.playerTimeDisplayFormat,
  transcriptTimeDisplayFormat: server?.transcriptTimeDisplayFormat ?? local.transcriptTimeDisplayFormat,
  expertReportTimeDisplayFormat: server?.expertReportTimeDisplayFormat ?? local.expertReportTimeDisplayFormat,
  filterTimeDisplayFormat: server?.filterTimeDisplayFormat ?? local.filterTimeDisplayFormat,
  interruptionMinDurationMs: server?.interruptionMinDurationMs ?? local.interruptionMinDurationMs,
  interruptionMaxDurationMs: server?.interruptionMaxDurationMs ?? local.interruptionMaxDurationMs,
  pauseMinDurationMs: server?.pauseMinDurationMs ?? local.pauseMinDurationMs,
  insertMinDurationMs: server?.insertMinDurationMs ?? local.insertMinDurationMs,
  insertSeriesMinCount: server?.insertSeriesMinCount ?? local.insertSeriesMinCount,
  insertMaxGapMs: server?.insertMaxGapMs ?? local.insertMaxGapMs,
});

/**
 * Слияние конфигов по code: темы по theme.code, внутри темы colorParameter по param.code.
 * Локальный конфиг — база, данные с бекенда перезаписывают совпадающие по code.
 */
const mergeConfigsByCode = (
  local: ProjectConfigResponse,
  server: ProjectConfigResponse | null,
): ProjectConfigResponse => {
  if (!server?.themes?.length) {
    // Общие настройки приходят тем же ответом и от наличия тем не зависят
    return { ...local, ...mergeGeneralSettings(local, server) };
  }
  const localThemesByCode = new Map(local.themes.map((t) => [t.code, t]));
  const serverThemesByCode = new Map(server.themes.map((t) => [t.code, t]));

  const mergedThemes: ThemeData[] = local.themes.map((localTheme) => {
    const serverTheme = serverThemesByCode.get(localTheme.code);
    return mergeThemeByCode(localTheme, serverTheme);
  });

  // Добавляем темы с бекенда, которых нет в локальном (по code)
  for (const serverTheme of server.themes) {
    if (!localThemesByCode.has(serverTheme.code)) {
      mergedThemes.push(serverTheme);
    }
  }

  return {
    projectName: server.projectName ?? local.projectName,
    favicon: server.favicon ?? local.favicon,
    ...mergeGeneralSettings(local, server),
    themes: mergedThemes,
  };
};

/**
 * Получение конфигурации проекта с backend с merge по code с локальным theme-config.json.
 * Темы сливаются по theme.code, параметры цвета — по colorParameter[].code.
 *
 * @returns Promise с конфигурацией (themes — массив ThemeData, colorParameter с code в snake_case)
 */
export const fetchThemeConfig = async (): Promise<ProjectConfigResponse> => {
  try {
    const { data } = await apiClient.get<ProjectConfigResponse>('/project-config');

    return mergeConfigsByCode(LOCAL_PROJECT_CONFIG, data ?? null);
  } catch (error) {
    console.warn('Failed to fetch theme config from server, using local fallback:', error);
    return LOCAL_PROJECT_CONFIG;
  }
};

/**
 * Изменение общих настроек системы. Значение действует на всех пользователей.
 */
export const updateGeneralSettings = async (settings: GeneralSettingsUpdate): Promise<GeneralSettingsResponse> => {
  const { data } = await apiClient.put<GeneralSettingsResponse>('/general-settings', settings);
  return data;
};
